/**
 * Overworld animals, round 3 (vanilla Java numbers):
 *  - rabbit: 3 HP, hops, runs from players within 8 blocks, coat by biome;
 *    drops hide 0-1, raw rabbit 0-1, a foot 10% of the time (player kills).
 *  - goat: 10 HP, wheat, milkable; every 30-300 s it rams a still target 4-7
 *    blocks away (2 damage, heavy knockback); ramming a solid block instead
 *    knocks a horn off (two per goat).
 *  - turtle: 30 HP, slow on land, swims; seagrass; a baby that grows up sheds
 *    a scute. (Vanilla lays eggs on its home beach; here the baby is born.)
 *  - mooshroom: a cow that gives mushroom stew to a bowl and turns into a cow
 *    (5 red mushrooms) when sheared.
 *  - polar bear: 30 HP, neutral; hit one, or come near a cub, and the adults
 *    attack for 6. Cod / salmon drops.
 *  - ocelot: 10 HP, shy until fed raw fish (1 in 3 per fish), hunts chickens.
 *  - snow golem: 4 HP, pelts hostile mobs with snowballs, leaves a snow trail,
 *    melts in rain; shears take the pumpkin off.
 */
import { AIR, BLOCKS, IS_SOLID, IS_WATERY, RED_MUSHROOM_ID, SEAGRASS_ID, SNOW_LAYER_ID } from '../../blocks';
import { I } from '../../game/itemIds';
import { stackOf, type MobType, type Stack } from '../../game/types';
import { Animal } from '../animals';
import type { Mob, MobCtx, MobSpec } from '../mob';
import { mobs3Hooks, nearestMob, ri } from './hooks';

const d2p = (m: Mob, ctx: MobCtx): number => {
  const p = ctx.host.player;
  return (p.x - m.x) ** 2 + (p.y - m.y) ** 2 + (p.z - m.z) ** 2;
};

/** Animal with its own loot table instead of the raw / cooked meat pair. */
abstract class Beast extends Animal {
  protected raw = 0;
  protected cooked = 0;
  constructor(id: number, type: MobType, x: number, y: number, z: number, spec: MobSpec, rnd: () => number) {
    super(id, type as 'pig', x, y, z, spec, rnd);
  }
  protected abstract drops(rnd: () => number, byPlayer: boolean, burning: boolean): Stack[];
  override loot(rnd: () => number, byPlayer = false): Stack[] {
    return this.noDrops() ? [] : this.drops(rnd, byPlayer, this.fireTicks > 0);
  }
}

// ------------------------------------------------------------------ rabbit

export const RABBIT_COATS = ['brown', 'white', 'black', 'white_splotched', 'gold', 'salt'] as const;

/** Vanilla Rabbit.getRandomRabbitVariant: snow 80% white / 20% splotched, desert gold, else 50 brown / 40 salt / 10 black. */
export function rabbitCoat(biome: string, r: number): number {
  const b = biome.toLowerCase();
  if (/snow|frozen|ice|grove/.test(b)) return r < 0.8 ? 1 : 3;
  if (b.includes('desert')) return 4;
  return r < 0.5 ? 0 : r < 0.9 ? 5 : 2;
}

export class Rabbit extends Beast {
  coat: number;
  constructor(id: number, x: number, y: number, z: number, rnd: () => number) {
    super(id, 'rabbit', x, y, z, { width: 0.4, height: 0.5, eye: 0.4, health: 3, speed: 0.3 }, rnd);
    this.coat = rabbitCoat('', rnd());
    this.color = this.coat;
    this.panicMod = 2.2;
  }
  protected override ai(ctx: MobCtx): void {
    this.color = this.coat;
    // AvoidEntityGoal(Player, 8 blocks, 2.2x)
    if (this.panicTicks === 0 && this.loveTicks === 0 && ctx.targetable() && d2p(this, ctx) < 64) {
      this.ageTick(ctx);
      const p = ctx.host.player;
      if (!this.navAway(ctx, p.x, p.z, 2.2, 3, true)) this.setMove(this.x - p.x, this.z - p.z, 2.2);
    } else super.ai(ctx);
    // rabbits travel in hops
    if (this.moveMod > 0 && this.onGround && !this.inWater) this.vy = this.moveMod > 1.5 ? 0.3 : 0.24;
  }
  override inherit(a: Animal, b: Animal, rnd: () => number): void {
    this.coat = ((rnd() < 0.5 ? a : b) as Rabbit).coat ?? this.coat;
  }
  protected override saveMore(o: Record<string, number>): void { o.v = this.coat; }
  protected override loadMore(o: Record<string, number>): void { this.coat = (o.v | 0) % RABBIT_COATS.length; this.color = this.coat; }
  protected drops(rnd: () => number, byPlayer: boolean, burning: boolean): Stack[] {
    const out: Stack[] = [];
    if (rnd() < 0.5) out.push(stackOf(I.RABBIT_HIDE, 1));
    if (rnd() < 0.5) out.push(stackOf(burning ? I.COOKED_RABBIT : I.RABBIT, 1));
    if (byPlayer && rnd() < 0.1) out.push(stackOf(I.RABBIT_FOOT, 1));
    return out;
  }
}

// -------------------------------------------------------------------- goat

/** Blocks a ramming goat loses a horn against (vanilla tag snaps_goat_horn: logs, stone, packed ice, ores). */
function snapsHorn(id: number): boolean {
  const n = BLOCKS[id]?.name ?? '';
  return /Log|Stone|Packed Ice|Ore|Stem/.test(n);
}

export const RAM_PREPARE = 20;

export class Goat extends Beast {
  horns = 2;
  /** Ticks until it may ram again (vanilla 600-6000). */
  ramCd: number;
  /** 0 idle, 1 lowering its head, 2 charging. */
  ramState = 0;
  private ramT = 0;
  private ramDX = 0; private ramDZ = 0;
  private ramMob: Mob | null = null;
  constructor(id: number, x: number, y: number, z: number, rnd: () => number) {
    super(id, 'goat', x, y, z, { width: 0.9, height: 1.3, eye: 1.1, health: 10, speed: 0.2 }, rnd);
    this.ramCd = ri(rnd, 600, 6000);
    this.noFallDamage = true; // vanilla goats take 10 less fall damage; they do not die on their mountains
  }
  private pose(): void { this.color = this.horns | (this.ramState << 2); }

  protected override ai(ctx: MobCtx): void {
    this.pose();
    if (this.baby || this.panicTicks > 0 || this.loveTicks > 0) { this.ramState = 0; super.ai(ctx); return; }
    if (this.ramState === 0) {
      if (this.ramCd > 0) this.ramCd--;
      else if (this.phase(ctx, 10)) this.pickRam(ctx);
      if (this.ramState === 0) { super.ai(ctx); return; }
    }
    this.ageTick(ctx);
    const p = ctx.host.player;
    const t = this.ramMob;
    if (this.ramState === 1) {
      const tx = t ? t.x : p.x, tz = t ? t.z : p.z;
      this.faceToward(tx, tz);
      if (++this.ramT >= RAM_PREPARE) {
        const d = Math.hypot(tx - this.x, tz - this.z) || 1;
        this.ramDX = (tx - this.x) / d; this.ramDZ = (tz - this.z) / d;
        this.ramState = 2; this.ramT = 0;
      }
      return;
    }
    // charging in a straight line
    this.faceToward(this.x + this.ramDX, this.z + this.ramDZ);
    this.setMove(this.ramDX, this.ramDZ, 3);
    const hx = t ? t.x : p.x, hy = t ? t.y : p.y, hz = t ? t.z : p.z;
    const hit = (hx - this.x) ** 2 + (hz - this.z) ** 2 < 1.2 * 1.2 && Math.abs(hy - this.y) < 1.5;
    if (hit && (t ? t.alive : ctx.targetable())) {
      if (t) mobs3Hooks.hurtMob(t, 2, this, 1.2);
      else ctx.hurtPlayer(this, 2, 2.5);
      this.endRam(ctx);
      return;
    }
    if (++this.ramT > 8 && this.collidedH) {
      const bx = Math.floor(this.x + this.ramDX * (this.width / 2 + 0.3)), bz = Math.floor(this.z + this.ramDZ * (this.width / 2 + 0.3));
      if (this.horns > 0 && snapsHorn(this.w.getBlock(bx, Math.floor(this.y + 0.5), bz))) {
        this.horns--;
        ctx.host.dropItem(stackOf(I.GOAT_HORN, 1), this.x, this.y + 0.8, this.z);
      }
      this.endRam(ctx);
    } else if (this.ramT > 40) this.endRam(ctx);
  }

  private endRam(ctx: MobCtx): void {
    this.ramState = 0; this.ramT = 0; this.ramMob = null;
    this.ramCd = ri(ctx.host.rnd, 600, 6000);
    this.pose();
  }

  /** Vanilla PrepareRamNearestTarget: something that is not a goat, 4-7 blocks away, in sight. */
  private pickRam(ctx: MobCtx): void {
    const p = ctx.host.player;
    const ok = (x: number, y: number, z: number): boolean => {
      const d = Math.hypot(x - this.x, z - this.z);
      return d >= 4 && d <= 7 && Math.abs(y - this.y) < 2 && ctx.los(this.x, this.eyeY, this.z, x, y + 0.8, z);
    };
    this.ramMob = null;
    if (ctx.targetable() && ok(p.x, p.y, p.z)) { this.ramState = 1; this.ramT = 0; return; }
    const m = nearestMob(this, 7, (o) => o.type !== 'goat' && o.type !== 'shulker' && o.width < 2 && ok(o.x, o.y, o.z));
    if (m) { this.ramMob = m; this.ramState = 1; this.ramT = 0; }
  }

  protected override saveMore(o: Record<string, number>): void { o.h = this.horns; if (this.ramState) o.r = this.ramState; }
  protected override loadMore(o: Record<string, number>): void { this.horns = o.h === undefined ? 2 : o.h | 0; this.color = this.horns | ((o.r | 0) << 2); }
  protected drops(): Stack[] { return []; }
}

// ------------------------------------------------------------------ turtle

export class Turtle extends Beast {
  private tx = 0; private ty = 0; private tz = 0; private retarget = 0;
  constructor(id: number, x: number, y: number, z: number, rnd: () => number) {
    super(id, 'turtle', x, y, z, { width: 1.2, height: 0.4, eye: 0.3, health: 30, speed: 0.12 }, rnd);
    this.noFallDamage = true;
  }
  protected override floats(): boolean { return false; }
  private water(x: number, y: number, z: number): boolean { return IS_WATERY[this.w.getBlock(Math.floor(x), Math.floor(y), Math.floor(z))] === 1; }

  protected override ageTick(ctx: MobCtx): void {
    const was = this.baby;
    super.ageTick(ctx);
    // vanilla Turtle.ageBoundaryReached: a scute when it grows up
    if (was && !this.baby) ctx.host.dropItem(stackOf(I.TURTLE_SCUTE, 1), this.x, this.y + 0.3, this.z);
  }

  protected override ai(ctx: MobCtx): void {
    if (!this.inWater || this.mate || this.loveTicks > 0) { super.ai(ctx); return; }
    this.ageTick(ctx);
    const r = ctx.host.rnd;
    if (--this.retarget <= 0 || (this.tx - this.x) ** 2 + (this.ty - this.y) ** 2 + (this.tz - this.z) ** 2 < 1) {
      this.retarget = 60 + Math.floor(r() * 80);
      this.tx = this.x; this.ty = this.y; this.tz = this.z;
      for (let i = 0; i < 8; i++) {
        const x = this.x + (r() * 2 - 1) * 8, y = this.y + (r() * 2 - 1) * 3, z = this.z + (r() * 2 - 1) * 8;
        // mostly keeps to the water, now and then heads for the shore
        if (this.water(x, y, z) || (i === 7 && r() < 0.3)) { this.tx = x; this.ty = y; this.tz = z; break; }
      }
    }
    const dx = this.tx - this.x, dy = this.ty - this.y, dz = this.tz - this.z;
    const d = Math.hypot(dx, dy, dz) || 1;
    const sp = this.panicTicks > 0 ? 0.12 : 0.06;
    if (this.panicTicks > 0) this.panicTicks--;
    this.vx += (dx / d * sp - this.vx) * 0.15;
    this.vz += (dz / d * sp - this.vz) * 0.15;
    this.vy += (dy / d * sp - this.vy) * 0.15 + 0.02;
    if (this.collidedH) this.vy += 0.06; // climb out over the bank
    this.faceToward(this.tx, this.tz);
  }
  protected drops(rnd: () => number): Stack[] {
    const n = ri(rnd, 0, 2);
    return n ? [stackOf(SEAGRASS_ID, n)] : [];
  }
}

// --------------------------------------------------------------- mooshroom

export class Mooshroom extends Beast {
  constructor(id: number, x: number, y: number, z: number, rnd: () => number) {
    super(id, 'mooshroom', x, y, z, { width: 0.9, height: 1.4, eye: 1.3, health: 10, speed: 0.2 }, rnd);
  }
  /** Shears: five red mushrooms, and a plain cow is left standing there. */
  shear(): Stack[] {
    if (this.baby || !this.alive) return [];
    const cow = mobs3Hooks.spawn('cow', this.x, this.y, this.z);
    if (cow) { cow.yaw = this.yaw; cow.headYaw = this.headYaw; cow.health = Math.min(cow.maxHealth, this.health); cow.customName = this.customName; cow.persistent = this.persistent; }
    this.host?.effect('explosion_small', this.x, this.y + this.height / 2, this.z);
    this.removed = true;
    return [stackOf(RED_MUSHROOM_ID, 5)];
  }
  protected drops(rnd: () => number, _by: boolean, burning: boolean): Stack[] {
    const out = [stackOf(burning ? I.COOKED_BEEF : I.BEEF, ri(rnd, 1, 3))];
    const l = ri(rnd, 0, 2);
    if (l) out.push(stackOf(I.LEATHER, l));
    return out;
  }
}

// -------------------------------------------------------------- polar bear

export class PolarBear extends Beast {
  anger = 0;
  private attackCd = 0;
  private stand = 0;
  protected override saveMore(o: Record<string, number>): void { if (this.stand > 0) o.c = 1; }
  protected override loadMore(o: Record<string, number>): void { this.color = o.c ? 1 : 0; }
  constructor(id: number, x: number, y: number, z: number, rnd: () => number) {
    super(id, 'polar_bear', x, y, z, { width: 1.4, height: 1.4, eye: 1.2, health: 30, speed: 0.25 }, rnd);
  }
  override onHurtBy(fromX: number, fromZ: number, rnd: () => number): void {
    if (this.baby) { super.onHurtBy(fromX, fromZ, rnd); }
    // the whole family answers (HurtByTargetGoal.alertOthers)
    for (const m of mobs3Hooks.mobs()) {
      if (m instanceof PolarBear && !m.baby && (m.x - this.x) ** 2 + (m.z - this.z) ** 2 < 20 * 20) m.anger = 400;
    }
  }
  protected override ai(ctx: MobCtx): void {
    if (this.attackCd > 0) this.attackCd--;
    if (this.stand > 0) this.stand--;
    this.color = this.stand > 0 ? 1 : 0;
    if (this.baby) { super.ai(ctx); return; }
    // a player close to a cub is a threat (PolarBearNearestAttackableTargetGoal)
    if (this.anger === 0 && this.phase(ctx, 20) && ctx.targetable() && d2p(this, ctx) < 20 * 20
      && nearestMob(this, 8, (m) => m instanceof PolarBear && m.baby)) this.anger = 200;
    if (this.anger > 0 && ctx.targetable() && d2p(this, ctx) < 24 * 24) {
      this.anger--;
      this.ageTick(ctx);
      const p = ctx.host.player;
      this.aggressive = true;
      this.faceToward(p.x, p.z);
      this.lookAt(p.x, p.eyeY, p.z);
      if (d2p(this, ctx) < 3.5 * 3.5) this.stand = Math.max(this.stand, 6);
      if (this.inMeleeReach(ctx)) {
        if (this.attackCd === 0) { this.attackCd = 20; this.swingTicks = 1; ctx.hurtPlayer(this, 6, 0.4); }
      } else this.steerTo(ctx, p.x, p.y, p.z, 1.25, 3, false);
      return;
    }
    if (this.anger > 0) this.anger--;
    super.ai(ctx);
  }
  protected drops(rnd: () => number, _by: boolean, burning: boolean): Stack[] {
    const n = ri(rnd, 0, 2);
    if (!n) return [];
    return [stackOf(rnd() < 0.75 ? (burning ? I.COOKED_COD : I.COD) : (burning ? I.COOKED_SALMON : I.SALMON), n)];
  }
}

// ------------------------------------------------------------------ ocelot

export class Ocelot extends Beast {
  trusting = false;
  private attackCd = 0;
  private hunting = false;
  constructor(id: number, x: number, y: number, z: number, rnd: () => number) {
    super(id, 'ocelot', x, y, z, { width: 0.6, height: 0.7, eye: 0.6, health: 10, speed: 0.3 }, rnd);
    this.noFallDamage = true;
  }
  /** A raw fish offered to a wary ocelot: one in three wins its trust. Returns false when it already trusts. */
  offer(rnd: () => number): boolean {
    if (this.trusting || this.baby) return false;
    if (rnd() < 1 / 3) { this.trusting = true; this.host?.effect('heart', this.x, this.y + 0.8, this.z); } else this.host?.effect('poof', this.x, this.y + 0.8, this.z);
    return true;
  }
  protected override ai(ctx: MobCtx): void {
    if (this.attackCd > 0) this.attackCd--;
    if (this.panicTicks === 0 && this.loveTicks === 0 && !this.baby) {
      const held = (ctx.host.player as { heldItem?: number }).heldItem ?? ctx.host.heldItem?.() ?? 0;
      const fish = held === I.COD || held === I.SALMON;
      // wary of players (AvoidEntityGoal 10 blocks) unless trusting or being offered fish
      if (!this.trusting && !fish && ctx.targetable() && d2p(this, ctx) < 100) {
        this.ageTick(ctx);
        const p = ctx.host.player;
        if (!this.navAway(ctx, p.x, p.z, 1.33, 3, true)) this.setMove(this.x - p.x, this.z - p.z, 1.33);
        return;
      }
      // `aggressive` is cleared every tick: `hunting` keeps the chase going between the 10-tick scans
      const prey = this.phase(ctx, 10) || this.hunting ? nearestMob(this, 10, (m) => m.type === 'chicken' && m.alive) : null;
      this.hunting = prey !== null;
      if (prey) {
        this.ageTick(ctx);
        this.aggressive = true;
        this.faceToward(prey.x, prey.z);
        if ((prey.x - this.x) ** 2 + (prey.z - this.z) ** 2 < 1.4 * 1.4 && Math.abs(prey.y - this.y) < 1.2) {
          if (this.attackCd === 0) { this.attackCd = 20; this.swingTicks = 1; mobs3Hooks.hurtMob(prey, 3, this); }
        } else this.steerTo(ctx, prey.x, prey.y, prey.z, 1.2, 3, true);
        return;
      }
    }
    super.ai(ctx);
  }
  protected override saveMore(o: Record<string, number>): void { if (this.trusting) o.t = 1; }
  protected override loadMore(o: Record<string, number>): void { this.trusting = o.t === 1; }
  protected drops(): Stack[] { return []; }
}

// -------------------------------------------------------------- snow golem

let carved = -2;
/** The pumpkin a sheared snow golem drops (our pumpkin block stands in for the carved one). */
export function pumpkinDrop(): number {
  if (carved === -2) {
    carved = BLOCKS.findIndex((b) => b?.name === 'Carved Pumpkin');
    if (carved < 0) carved = BLOCKS.findIndex((b) => b?.name === 'Pumpkin');
  }
  return carved;
}

export class SnowGolem extends Beast {
  private throwCd = 0;
  private hunting = false;
  constructor(id: number, x: number, y: number, z: number, rnd: () => number) {
    super(id, 'snow_golem', x, y, z, { width: 0.7, height: 1.9, eye: 1.7, health: 4, speed: 0.2 }, rnd);
    this.persistent = true;
  }
  override get canFallInLove(): boolean { return false; }
  override onHurtBy(): void { /* golems do not panic */ }
  override xpValue(): number { return 0; }

  /** Shears take the pumpkin off. */
  shear(): Stack[] {
    if (this.sheared) return [];
    this.sheared = true;
    const id = pumpkinDrop();
    return id > 0 ? [stackOf(id, 1)] : [];
  }

  protected override ai(ctx: MobCtx): void {
    const host = ctx.host;
    // melts in the rain (vanilla: also in hot biomes)
    if (this.phase(ctx, 20) && (this.inWater || host.isRainingAt?.(Math.floor(this.x), Math.floor(this.y + 1), Math.floor(this.z)))) this.hurt(1);
    // snow trail
    if (this.onGround && this.phase(ctx, 4) && host.setBlock) {
      const bx = Math.floor(this.x), by = Math.floor(this.y + 0.01), bz = Math.floor(this.z);
      const below = this.w.getBlock(bx, by - 1, bz);
      if (this.w.getBlock(bx, by, bz) === AIR && IS_SOLID[below] === 1 && BLOCKS[below]?.opaque !== false) host.setBlock(bx, by, bz, SNOW_LAYER_ID);
    }
    if (this.throwCd > 0) this.throwCd--;
    const foe = this.phase(ctx, 5) || this.hunting ? nearestMob(this, 10, (m) => mobs3Hooks.isHostile(m.type) && m.type !== 'creeper'
      && ctx.los(this.x, this.eyeY, this.z, m.x, m.y + m.eyeHeight, m.z)) : null;
    this.hunting = foe !== null;
    if (foe) {
      this.aggressive = true;
      this.faceToward(foe.x, foe.z);
      this.lookAt(foe.x, foe.y + foe.eyeHeight, foe.z);
      if (this.throwCd === 0) {
        this.throwCd = 20;
        this.swingTicks = 1;
        // a snowball: knocks back, hurts only blazes (3)
        if (!mobs3Hooks.hurtMob(foe, foe.type === 'blaze' ? 3 : 0, this, 0.4)) foe.knockback(foe.x - this.x, foe.z - this.z, 0.4, host.rnd);
        host.effect('poof', foe.x, foe.y + foe.height * 0.6, foe.z);
      }
      return;
    }
    this.stroll(ctx, 1, 3, true);
    this.lookAtNearPlayer(ctx);
  }
  protected drops(rnd: () => number): Stack[] {
    const n = ri(rnd, 0, 15);
    return n ? [stackOf(I.SNOWBALL, n)] : [];
  }
}

/** Two snow blocks under a freshly placed pumpkin: true when the column at (x, y, z) is a snow golem. */
export function snowGolemPattern(get: (x: number, y: number, z: number) => number, x: number, y: number, z: number, snowBlockId: number): boolean {
  return get(x, y - 1, z) === snowBlockId && get(x, y - 2, z) === snowBlockId;
}
